import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { personLabel } from '../../components/ApprovalLevels.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface RankingRules {
  readonly weights: Record<string, number>;
  readonly factorOrder: readonly string[];
  readonly normalized: boolean;
  readonly sum: number;
}
interface SecurityPosture {
  readonly enforceDocumentSecurity: boolean;
  readonly enforceFieldSecurity: boolean;
  readonly dataMaskingEnabled: boolean;
  readonly classificationLevels: readonly string[];
  readonly classificationDistribution: readonly { readonly classificationLevel: string; readonly count: number }[];
  readonly auditRetentionYears: number;
}
interface AuditRow {
  readonly searchAuditLogId: string;
  readonly eventType: string;
  readonly entityType: string;
  readonly action: string;
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly eventTimestamp: string;
}

const WEIGHT_KEYS = ['relevance', 'recency', 'popularity', 'usage', 'priority', 'department'];

/**
 * M39 — Search Administration Console (§13/§27). The governor's surface for
 * tuning the ranking model (T520 config) and reviewing the security posture and
 * the immutable audit ledger (T522, BR-07). Ranking-factor weights are edited and
 * saved under GOVERNANCE_MANAGE — distinct by SoD from the searchers and index
 * admins. Viewing the posture + audit trail needs GOVERNANCE_VIEW / AUDIT_VIEW.
 * Weights are validated server-side (0..1); a 403 surfaces the required
 * permission.
 */
export function SearchAdministrationConsole(): ReactNode {
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rules, setRules] = useState<RankingRules | null>(null);
  const [posture, setPosture] = useState<SecurityPosture | null>(null);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      apiRequest<{ data: RankingRules }>(`/v1/search/governance/ranking`, { token }),
      apiRequest<{ data: SecurityPosture }>(`/v1/search/governance/security`, { token }),
      apiRequest<{ data: AuditRow[]; total: number }>(`/v1/search/audit?limit=25`, { token }),
    ])
      .then(([r, s, a]) => {
        if (!alive) return;
        setRules(r.data); setWeights({ ...r.data.weights }); setPosture(s.data); setAudit(a.data); setError(null);
      })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reload]);

  const saveRanking = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    setBusy(true);
    try {
      const r = await apiRequest<{ data: { weights: Record<string, number>; sum: number } }>(`/v1/search/governance/ranking`, {
        method: 'PUT',
        body: { weights },
        token,
      });
      setNotice(t('m39.admin.rankingUpdated', { sum: r.data.sum }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m39.admin.notAuthorised'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <main aria-label="search-administration-console">
      <h1>{t('m39.admin.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading || !rules || !posture ? <p>{t('common.loading')}</p> : (
        <>
          <section aria-label="ranking-rules">
            <h2>{t('m39.admin.rankingWeights')}</h2>
            <p aria-label="ranking-sum">{t('m39.admin.currentSum', { sum: rules.sum, state: rules.normalized ? t('m39.admin.normalized') : t('m39.admin.notNormalized') })}</p>
            <form aria-label="ranking-form" onSubmit={(e) => void saveRanking(e)}>
              {WEIGHT_KEYS.map((k) => (
                <label key={k}>{k}
                  <input type="number" step="0.05" min="0" max="1" aria-label={`weight-${k}`}
                    value={weights[k] ?? 0}
                    onChange={(e) => setWeights((w) => ({ ...w, [k]: Number(e.target.value) }))} />
                </label>
              ))}
              <Button variant="primary" type="submit" aria-label="submit-ranking" disabled={busy}>{busy ? t('m39.common.saving') : t('m39.admin.saveRanking')}</Button>
            </form>
            {actionError && <p role="alert" aria-label="ranking-error">{actionError}</p>}
            {notice && <p aria-label="ranking-notice">{notice}</p>}
            <ol aria-label="factor-order">
              {rules.factorOrder.map((f) => <li key={f} data-factor={f}>{f}</li>)}
            </ol>
          </section>

          <section aria-label="security-posture">
            <h2>{t('m39.admin.securityPosture')}</h2>
            <dl>
              <dt>{t('m39.admin.documentSecurity')}</dt><dd aria-label="sec-doc">{posture.enforceDocumentSecurity ? t('m39.admin.enforced') : t('m39.admin.off')}</dd>
              <dt>{t('m39.admin.fieldSecurity')}</dt><dd aria-label="sec-field">{posture.enforceFieldSecurity ? t('m39.admin.enforced') : t('m39.admin.off')}</dd>
              <dt>{t('m39.admin.dataMasking')}</dt><dd aria-label="sec-mask">{posture.dataMaskingEnabled ? t('m39.admin.on') : t('m39.admin.off')}</dd>
              <dt>{t('m39.admin.auditRetention')}</dt><dd aria-label="sec-retention">{t('m39.admin.years', { years: posture.auditRetentionYears })}</dd>
            </dl>
            <DataTable
              testId="classification-distribution"
              ariaLabel="classification-distribution"
              rows={posture.classificationDistribution}
              rowKey={(d) => d.classificationLevel}
              emptyMessage={t('m39.common.noIndexedContent')}
              columns={[
                { key: 'classificationLevel', header: t('m39.common.classification'), render: (d) => d.classificationLevel },
                { key: 'count', header: t('m39.admin.items'), render: (d) => d.count },
              ]}
            />
          </section>

          <section aria-label="audit-trail">
            <h2>{t('m39.admin.auditTrail')}</h2>
            <DataTable
              testId="audit-table"
              ariaLabel="audit-table"
              rows={audit}
              rowKey={(a) => a.searchAuditLogId}
              emptyMessage={t('m39.admin.noAudit')}
              columns={[
                { key: 'eventType', header: t('m39.admin.colEvent'), render: (a) => a.eventType },
                { key: 'entityType', header: t('m39.admin.colEntity'), render: (a) => a.entityType },
                { key: 'action', header: t('m39.admin.colAction'), render: (a) => a.action },
                { key: 'actor', header: t('m39.admin.colActor'), render: (a) => personLabel(a.actorName, a.actorCode) ?? '—' },
                { key: 'eventTimestamp', header: t('m39.admin.colWhen'), render: (a) => at(a.eventTimestamp) },
              ]}
            />
          </section>
        </>
      )}
    </main>
  );
}
